// Single source of truth for every site-wide SEO / entity fact.
//
// Everything here ends up in <head>, in JSON-LD structured data, in the
// sitemap and in llms.txt. Google builds its understanding of the Sigma
// Enterprises *entity* from the consistency of these values against the
// Google Business Profile and third-party directories, so any change here
// must be mirrored there (and vice versa). Do not invent facts.

export const SITE_URL = "https://sigmapapermart.com";

export const BUSINESS = {
  // Legal / trading name. Must match the Google Business Profile exactly.
  name: "Sigma Enterprises",
  // Brand the domain is known by; used as an alternate entity name.
  alternateName: "Sigma Paper Mart",
  legalName: "Sigma Enterprises",
  // Trading since 1996 — confirmed by the owner. This is the strongest
  // experience signal the business has, so it belongs in the entity data, the
  // About copy and the Google Business Profile description alike.
  foundingYear: "1996",
  description:
    "Wholesale paper and board distributor in New Delhi supplying art card, SBS, kraft, duplex, copier, textured, specialty and adhesive paper to printers, packaging converters and traders across India.",

  phone: "+91 99992 42566",
  phoneE164: "+919999242566",
  phoneHref: "tel:+919999242566",
  whatsapp: "919999242566",

  address: {
    street: "B-33/13, Paper Market, Ramesh Nagar, Raja Garden",
    locality: "New Delhi",
    region: "Delhi",
    postalCode: "110015",
    country: "IN",
    countryName: "India",
  },

  geo: { lat: 28.6499373, lng: 77.1271445 },

  mapLink: "https://maps.app.goo.gl/HkBcHffLwKRvUgNH7",
  mapEmbed: "https://www.google.com/maps?q=28.6499373,77.1271445&z=16&output=embed",

  // Mon–Sat, 10:00–20:00. Closed Sunday.
  openingHours: [
    {
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "10:00",
      closes: "20:00",
    },
  ],
  openingHoursText: "Mon – Sat · 10:00 – 20:00",

  // Areas the business actually supplies. Used for LocalBusiness areaServed
  // and for the service-area copy on the location pages.
  // Delivery territory, then export reach.
  //
  // The named countries are markets the business is registered and equipped to
  // supply — not a list of places it has shipped to. That distinction is the
  // whole point: naming a market is a statement of capability and is true,
  // whereas naming it as a completed trade would not be. It also keeps the
  // commercially sensitive part private, since nothing here indicates where
  // volume actually goes.
  //
  // Specific country names are used rather than "overseas" or "South Asia"
  // because that is how buyers search — "paper supplier from India to Nepal",
  // not "paper supplier to South Asia".
  areasServed: [
    "New Delhi",
    "Delhi NCR",
    "Gurugram",
    "Noida",
    "Faridabad",
    "Ghaziabad",
    "India",
  ],

  // Export markets the business can supply. Order is deliberate: nearest and
  // most practical first, since that is both the commercial reality of freight
  // cost and the order a buyer scanning the page would find useful.
  exportMarkets: [
    "Nepal",
    "Bhutan",
    "Bangladesh",
    "Sri Lanka",
    "Maldives",
    "United Arab Emirates",
    "Europe",
  ],

  // Importer Exporter Code registration, issued by India's DGFT. The number
  // itself is deliberately not published — the registration is the signal
  // buyers need, and serious ones ask for the number directly.
  iecRegistered: true,

  priceRange: "₹₹",
  currenciesAccepted: "INR",
};

// Canonical single-line NAP. Use this string verbatim in every directory
// listing — citation consistency is a direct local-pack ranking factor.
export const NAP_STRING = `${BUSINESS.name}, ${BUSINESS.address.street}, ${BUSINESS.address.locality} — ${BUSINESS.address.postalCode}, ${BUSINESS.address.countryName}. ${BUSINESS.phone}`;

// Default social / sameAs profiles. Every entry must be a real, live profile
// that names this business — a dead or wrong sameAs weakens the entity graph
// rather than strengthening it. Add profiles here only once they exist.
export const SAME_AS = [BUSINESS.mapLink];

export const DEFAULTS = {
  title: "Sigma Enterprises — Paper & Board Distributor in Delhi",
  description: BUSINESS.description,
  image: `${SITE_URL}/assets/og/og-default.jpg`,
  imageAlt: "Sigma Enterprises paper and board warehouse, New Delhi",
  locale: "en_IN",
  twitterCard: "summary_large_image",
};

// Build an absolute URL from a route path. Canonical form is no trailing
// slash (the root is the sole exception); .htaccess enforces the same.
export function absUrl(path = "/") {
  if (/^https?:\/\//i.test(path)) return path;
  const clean = `/${String(path).replace(/^\/+/, "").replace(/\/+$/, "")}`;
  return clean === "/" ? SITE_URL + "/" : SITE_URL + clean;
}
